import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  Signal
} from '@angular/core';
import { User } from '@shared/api/api-loop/models/user';
import { DropdownItem } from '@shared/ui/dropdown/interfaces/dropdown-item';
import { SmartContactsApiService } from '@shared/modules/contacts/shell/contacts-api-cache/smart-contacts-api.service';
import { isGroupModel } from '@shared/modules/contacts/common/helpers/is-group-contact';
import { ContactBase } from '@shared/api/api-loop/models/contact-base';
import { ParticipantSearchNavigationService } from '@shared/modules/search/services/participant-search-navigation.service';
import { UserAuthService } from '@shared/modules/auth/auth-data-access/user-auth.service';
import { SettingsScope } from '@shared/modules/auth/settings/common/settings-scope';
import { AISettingsScopeProperty } from '@shared/modules/auth/settings/common/ai-settings-scope';
import {
  AdditionalTranslateFromLanguage,
  TranslateResponse
} from '@shared/modules/smart-commands/common/translations/interfaces/responses';
import { ChannelType } from '@shared/modules/comments/common/constants/channel-type';
import { MailCommentOperationsService } from '@shared/modules/comments/components/mail-comment/mail-comment-operations/mail-comment-operations.service';
import { DateFormat } from '@shared/shared/fancy-date/common/enumerators/date-format';
import { isConversationClosed } from '@shared/modules/conversations/common/helpers/is-conversation-closed';
import { ConversationActionService } from '@shared/services/data/conversation-action/conversation-action.service';
import { ActionEnum, TagType } from '@shared/api/api-loop/models';
import { TagApiService } from '@shared/api/api-loop/services';
import { TrackingService } from '@shared/modules/tracking/tracking.service';
import { CommentDraftModel, CommentMailModel, isDraftComment } from '@dta/shared/models-api-loop/comment/comment.model';
import { defer, Observable, of, ReplaySubject } from 'rxjs';
import { map, publishReplay, refCount, switchMap, take } from 'rxjs/operators';
import { ConversationModel } from '@dta/shared/models-api-loop/conversation-card/conversation/conversation.model';
import { KeyboardActionType, KeyboardShortcut, shortcutByActionType } from '@dta/shared/models/keyboard-shortcut.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TagModel } from '@dta/shared/models-api-loop/tag.model';

@UntilDestroy()
@Component({
  selector: 'loop-mail-comment-header',
  templateUrl: './mail-comment-header.component.html',
  styleUrls: ['./mail-comment-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class MailCommentHeaderComponent implements OnDestroy {
  private readonly mailCommentActionsService: MailCommentOperationsService = inject(MailCommentOperationsService);
  private readonly tagApiService: TagApiService = inject(TagApiService);
  private readonly conversationActionService: ConversationActionService = inject(ConversationActionService);
  private readonly smartContactsApiService: SmartContactsApiService = inject(SmartContactsApiService);
  private readonly trackingService: TrackingService = inject(TrackingService);
  private readonly userAuthService: UserAuthService = inject(UserAuthService);
  private readonly participantSearchNavigationService: ParticipantSearchNavigationService = inject(
    ParticipantSearchNavigationService
  );

  protected readonly comment$: ReplaySubject<CommentMailModel | CommentDraftModel> = new ReplaySubject<
    CommentMailModel | CommentDraftModel
  >(1);

  @Input() set comment(comment: CommentMailModel | CommentDraftModel) {
    this.comment$.next(comment);
  }

  @Input() commentDrafts?: CommentDraftModel[];
  @Input() isCommentCollapsed: boolean;
  @Input() conversation?: ConversationModel;
  @Output() collapse: EventEmitter<void> = new EventEmitter<void>();
  @Output() popOut: EventEmitter<void> = new EventEmitter<void>();
  @Output() openQuickReply: EventEmitter<CommentMailModel> = new EventEmitter<CommentMailModel>();
  @Output() closeQuickReply: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() commentMailTranslate: EventEmitter<TranslateResponse> = new EventEmitter<TranslateResponse>();
  readonly areActionsVisibleSignal: Signal<boolean> = input(true, { alias: 'areActionsVisible' });

  protected readonly CommentDraft_Type: string = CommentDraftModel.type;
  protected readonly DateFormat_RelativeNowOffsetShort: DateFormat.relativeNowOffsetShort =
    DateFormat.relativeNowOffsetShort;

  protected areRecipientsFullyVisible: boolean = false;

  protected readonly ChannelType_LoopSpam: typeof ChannelType.loopSpam = ChannelType.loopSpam;

  protected get isClosed(): boolean {
    return isConversationClosed(this.conversation);
  }

  protected readonly replyTo$: Observable<ContactBase> = this.comment$.pipe(
    map(comment => comment.replyTo?.resources?.[0])
  );

  protected readonly author$: Observable<User> = this.comment$.pipe(
    switchMap((comment: CommentMailModel | CommentDraftModel) => {
      if (!isDraftComment(comment)) {
        return of(comment.author);
      }

      if (!comment.sendAsGroupId) {
        return of(comment.author);
      }

      return this.smartContactsApiService.getSingle(comment.sendAsGroupId).pipe(
        map(contact => {
          if (!isGroupModel(contact)) {
            return contact;
          }

          return (
            contact.allowedImpersonatedSenders?.resources?.find(impersonated => {
              return impersonated.email === comment.sendAs;
            }) ?? comment.author
          );
        })
      );
    }),
    publishReplay(1),
    refCount()
  );

  protected readonly hasAttachment$: Observable<boolean> = this.comment$.pipe(
    map(comment => {
      return comment.attachments?.resources?.filter(attachment => !attachment.hidden).length > 0;
    })
  );

  protected readonly dropdownOptions$: Observable<DropdownItem[]> = this.comment$.pipe(
    map(comment => {
      const options: DropdownItem[] = [];

      if (!this.conversation) {
        return options;
      }

      if (!this.isClosed) {
        options.push(
          {
            title: 'Reply all',
            action: () =>
              defer(() => {
                if (comment.body?.content) {
                  return of(comment);
                }

                return this.mailCommentActionsService.getCommentHtml$(comment);
              })
                .pipe(take(1))
                .subscribe(() => {
                  this.mailCommentActionsService.replyAction({
                    conversation: this.conversation,
                    replyToComment: comment,
                    groupId: this.conversation?.findGroupInShareList()?.id,
                    replyType: 'replyAll',
                    closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
                    trackingAction: 'ReplyAll'
                  });
                }),
            icon: 'reply-all',
            shortcut: new KeyboardShortcut(shortcutByActionType[KeyboardActionType.REPLY_TO_ALL]).stringRepresentation
          },
          {
            title: 'Reply',
            action: () =>
              defer(() => {
                if (comment.body?.content) {
                  return of(comment);
                }

                return this.mailCommentActionsService.getCommentHtml$(comment);
              })
                .pipe(take(1))
                .subscribe(() => {
                  this.mailCommentActionsService.replyAction({
                    conversation: this.conversation,
                    replyToComment: comment,
                    groupId: this.conversation?.findGroupInShareList()?.id,
                    replyType: 'replyOne',
                    closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
                    trackingAction: 'Reply'
                  });
                }),
            icon: 'reply',
            shortcut: new KeyboardShortcut(shortcutByActionType[KeyboardActionType.REPLY]).stringRepresentation
          }
        );
      }

      options.push({
        title: 'Reply as new conversation',
        action: () =>
          defer(() => {
            if (comment.body?.content) {
              return of(comment);
            }

            return this.mailCommentActionsService.getCommentHtml$(comment);
          })
            .pipe(take(1))
            .subscribe(() => {
              this.mailCommentActionsService.replyAction({
                conversation: this.conversation,
                replyToComment: comment,
                groupId: this.conversation?.findGroupInShareList()?.id,
                replyType: 'replyOne',
                isForceSplit: true,
                closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
                trackingAction: 'ReplayAsNewConversation'
              });
            }),
        icon: 'reply'
      });

      if (!this.isClosed) {
        options.push(
          {
            title: 'Forward',
            action: () =>
              defer(() => {
                if (comment.body?.content) {
                  return of(comment);
                }

                return this.mailCommentActionsService.getCommentHtml$(comment);
              })
                .pipe(take(1))
                .subscribe(() => {
                  this.mailCommentActionsService.forwardAction({
                    conversation: this.conversation,
                    replyToComment: comment,
                    groupId: this.conversation?.findGroupInShareList()?.id,
                    closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
                    trackingAction: 'Forward'
                  });
                }),
            icon: 'forward',
            shortcut: new KeyboardShortcut(shortcutByActionType[KeyboardActionType.FORWARD]).stringRepresentation
          },
          {
            title: 'Forward as attachment',
            action: () =>
              defer(() => {
                if (comment.body?.content) {
                  return of(comment);
                }

                return this.mailCommentActionsService.getCommentHtml$(comment);
              })
                .pipe(take(1))
                .subscribe(() => {
                  this.mailCommentActionsService.forwardAsAttachment({
                    conversation: this.conversation,
                    replyToComment: comment,
                    groupId: this.conversation?.findGroupInShareList()?.id,
                    closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
                    trackingAction: 'ForwardAsAttachment'
                  });
                }),
            icon: 'forward'
          }
        );
      }

      options.push(
        {
          title: 'Forward as new conversation',
          action: () =>
            defer(() => {
              if (comment.body?.content) {
                return of(comment);
              }

              return this.mailCommentActionsService.getCommentHtml$(comment);
            })
              .pipe(take(1))
              .subscribe(() => {
                this.mailCommentActionsService.forwardAction({
                  conversation: this.conversation,
                  replyToComment: comment,
                  groupId: this.conversation?.findGroupInShareList()?.id,
                  forceSplit: true,
                  closeQuickReplyActionFn: () => this.closeQuickReply.emit(),
                  trackingAction: 'ForwardAsNewConversation'
                });
              }),
          icon: 'forward'
        },

        {
          title: 'Copy as attachment',
          action: () =>
            this.mailCommentActionsService.storeCommentToClipboard({
              conversation: this.conversation,
              replyToComment: comment
            }),
          icon: 'copy'
        },
        {
          title: '',
          isDelimiter: true
        },
        {
          title: 'Pin',
          action: () =>
            this.comment$
              .pipe(
                take(1),
                switchMap(comment => {
                  return this.conversationActionService.conversationAction({
                    conversationIds: [this.conversation?.id],
                    action: ActionEnum.PIN_COMMENT,
                    isSharedAction: true,
                    messageId: comment.id
                  });
                }),
                take(1),
                untilDestroyed(this)
              )
              .subscribe(),
          icon: 'thumbtack'
        },
        {
          title: 'Mark as unread',
          action: () =>
            this.comment$
              .pipe(
                take(1),
                switchMap(comment => {
                  comment.addTags([
                    TagModel.buildSystemTag(TagType.UNREAD),
                    TagModel.buildSystemTag(TagType.UNREAD_VIRTUAL)
                  ]);
                  return this.tagApiService.Tag_UpdateTags({
                    tags: [comment.tags]
                  });
                }),
                take(1),
                untilDestroyed(this)
              )
              .subscribe(),
          icon: 'envelope'
        },
        {
          title: 'Open in new window',
          action: () => this.openInNewWindow(),
          icon: 'arrow-up-right-from-square'
        },
        {
          title: 'Translate',
          icon: 'language',
          action: () =>
            this.userAuthService.currentSession$
              .pipe(
                switchMap(currentSession => {
                  return defer(() => {
                    if (comment.body?.content) {
                      return of(comment);
                    }

                    return this.mailCommentActionsService.getCommentHtml$(comment);
                  }).pipe(
                    take(1),
                    switchMap(() => {
                      return this.mailCommentActionsService.translateCommentMail({
                        conversation: this.conversation,
                        replyToComment: comment,
                        to_language:
                          currentSession?.userConfiguration?.[SettingsScope.AI]?.[
                            AISettingsScopeProperty.default_translate_language
                          ] ?? 'en'
                      });
                    }),
                    take(1)
                  );
                })
              )
              .pipe(take(1), untilDestroyed(this))
              .subscribe(translateResponse =>
                this.commentMailTranslate.next({
                  ...translateResponse,
                  from_language: AdditionalTranslateFromLanguage.detect
                })
              )
        },
        {
          title: 'Print',
          action: () =>
            this.mailCommentActionsService.printCommentMail({
              conversation: this.conversation,
              replyToComment: comment,
              groupId: this.conversation?.findGroupInShareList()?.id
            }),
          icon: 'print'
        },
        {
          title: 'Download message',
          action: () =>
            this.mailCommentActionsService.downloadCommentMail({
              conversation: this.conversation,
              replyToComment: comment,
              groupId: this.conversation?.findGroupInShareList()?.id
            }),
          icon: 'download'
        }
      );
      return options;
    })
  );

  protected searchEmails(contact: ContactBase): void {
    this.participantSearchNavigationService.openForContact(contact);
  }

  protected openInNewWindow(): void {
    this.comment$
      .pipe(
        take(1),
        switchMap(comment =>
          this.mailCommentActionsService.openCommentInNewWindow$(this.conversation.name, comment.id)
        ),
        take(1),
        untilDestroyed(this)
      )
      .subscribe();
  }

  toggleInformation(event: MouseEvent): void {
    if (this.isCommentCollapsed) {
      return;
    }

    this.trackingService.trackUserClick('MailCommentHeader', 'Toggle recipient information', {
      conversationId: this.conversation?.cardId,
      areRecipientsFullyVisible: !this.areRecipientsFullyVisible
    });

    event.preventDefault();
    event.stopPropagation();
    // if user is selecting text, prevent from closing the recipients information
    if (!!window.getSelection().toString()) {
      return;
    }
    this.areRecipientsFullyVisible = !this.areRecipientsFullyVisible;
  }

  ngOnDestroy(): void {
    this.comment$.complete();
  }
}
